import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
// ...existing code...
import './ManagementPages.css';

const ReportsPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportType, setReportType] = useState('dashboard');

  useEffect(() => {
    fetchReport();
  }, [reportType]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let data;
      if (reportType === 'dashboard') {
        data = await reportsAPI.getAdminDashboard();
      } else if (reportType === 'collections') {
        data = await reportsAPI.getCollectionsReport();
      } else if (reportType === 'performance') {
        data = await reportsAPI.getPerformanceReport();
      } else {
        data = await reportsAPI.getLedger();
      }
      
      setReport(data);
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reports</h1>
        <p>Generate and view system reports</p>
      </div>

      {/* Report Type Selector */}
      <div className="filters" style={{ marginBottom: '20px' }}>
        <button
          className={`filter-btn ${reportType === 'dashboard' ? 'active' : ''}`}
          onClick={() => setReportType('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`filter-btn ${reportType === 'collections' ? 'active' : ''}`}
          onClick={() => setReportType('collections')}
        >
          Collections
        </button>
        <button
          className={`filter-btn ${reportType === 'performance' ? 'active' : ''}`}
          onClick={() => setReportType('performance')}
        >
          Performance
        </button>
        <button
          className={`filter-btn ${reportType === 'ledger' ? 'active' : ''}`}
          onClick={() => setReportType('ledger')}
        >
          Ledger
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loader"></div>
      ) : report ? (
        <div className="report-container">
          {/* Dashboard Report */}
          {reportType === 'dashboard' && (
            <div>
              <div className="grid grid-4" style={{ marginBottom: '20px' }}>
                <div className="dashboard-card">
                  <div className="card-icon">👥</div>
                  <div className="card-content">
                    <div className="card-label">Total Agents</div>
                    <div className="card-value">{report.totalAgents || 0}</div>
                  </div>
                </div>
                <div className="dashboard-card">
                  <div className="card-icon">🏪</div>
                  <div className="card-content">
                    <div className="card-label">Total Retailers</div>
                    <div className="card-value">{report.totalRetailers || 0}</div>
                  </div>
                </div>
                <div className="dashboard-card">
                  <div className="card-icon">📋</div>
                  <div className="card-content">
                    <div className="card-label">Total Loans</div>
                    <div className="card-value">{report.totalLoans || 0}</div>
                  </div>
                </div>
                <div className="dashboard-card">
                  <div className="card-icon">💰</div>
                  <div className="card-content">
                    <div className="card-label">Collected</div>
                    <div className="card-value">₹{(report.totalCollected || 0).toLocaleString()}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-2" style={{ marginBottom: '20px' }}>
                <div className="dashboard-card">
                  <div className="card-icon">💵</div>
                  <div className="card-content">
                    <div className="card-label">Total Invested</div>
                    <div className="card-value">₹{(report.totalInvested || 0).toLocaleString()}</div>
                  </div>
                </div>
                <div className="dashboard-card">
                  <div className="card-icon">📊</div>
                  <div className="card-content">
                    <div className="card-label">Portfolio Value</div>
                    <div className="card-value">₹{(report.portfolioValue || 0).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Collections Report */}
          {reportType === 'collections' && report.collections && (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Total Collected</th>
                    <th>Collections Count</th>
                    <th>Average</th>
                  </tr>
                </thead>
                <tbody>
                  {report.collections.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.agentName}</td>
                      <td>₹{row.totalCollected?.toLocaleString() || 0}</td>
                      <td>{row.count || 0}</td>
                      <td>₹{(row.totalCollected / (row.count || 1))?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Performance Report */}
          {reportType === 'performance' && report.performance && (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Loans Submitted</th>
                    <th>Approved</th>
                    <th>Success Rate</th>
                    <th>Amount Distributed</th>
                  </tr>
                </thead>
                <tbody>
                  {report.performance.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.agentName}</td>
                      <td>{row.loansSubmitted || 0}</td>
                      <td>{row.loansApproved || 0}</td>
                      <td>{row.successRate || 0}%</td>
                      <td>₹{row.amountDistributed?.toLocaleString() || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Ledger */}
          {reportType === 'ledger' && report.ledger && (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Transaction Type</th>
                    <th>Description</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {report.ledger.map((row, idx) => (
                    <tr key={idx}>
                      <td>{new Date(row.date).toLocaleDateString()}</td>
                      <td>{row.type}</td>
                      <td>{row.description}</td>
                      <td>{row.debit ? `₹${row.debit.toLocaleString()}` : '-'}</td>
                      <td>{row.credit ? `₹${row.credit.toLocaleString()}` : '-'}</td>
                      <td><strong>₹{row.balance?.toLocaleString() || 0}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="no-data">No report data available</div>
      )}
    </div>
  );
};

export default ReportsPage;
